import type { CategorySort } from '@erp/shared-types';
import type { ReportDatasetRow } from '../ReportDataContext';

/** A value as a Number: itself when it is one, else its parse (maybe NaN). */
function asNumber(value: ReportDatasetRow[string] | undefined): number {
  return typeof value === 'number' ? value : Number(value);
}

/**
 * The rows with a finite Number on both axes, the axis values coerced to
 * Numbers — the numeric axes misplace points given numeric strings. A row
 * without one is dropped rather than plotted at a fabricated 0.
 */
export function numericPoints(
  rows: readonly ReportDatasetRow[],
  xKey: string,
  yKey: string,
): ReportDatasetRow[] {
  return rows.flatMap((row) => {
    const x = asNumber(row[xKey]);
    const y = asNumber(row[yKey]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return [];
    return [{ ...row, [xKey]: x, [yKey]: y }];
  });
}

/** The rows with `valueKey` coerced to a Number; one that isn't counts as 0. */
export function withNumericValues(
  rows: readonly ReportDatasetRow[],
  valueKey: string,
): ReportDatasetRow[] {
  return rows.map((row) => {
    const n = asNumber(row[valueKey]);
    return { ...row, [valueKey]: Number.isFinite(n) ? n : 0 };
  });
}

/**
 * The `max` rows of greatest value, the rest summed into one `otherLabel`
 * row — a pie's slices or a radar's sections. All of them when `max` is 0 or
 * there are no more than `max`. The values must already be Numbers.
 */
export function topWithOther(
  rows: readonly ReportDatasetRow[],
  nameKey: string,
  valueKey: string,
  max: number,
  otherLabel: string,
): ReportDatasetRow[] {
  if (max === 0 || rows.length <= max) return [...rows];
  const value = (row: ReportDatasetRow) => Number(row[valueKey]);
  const sorted = [...rows].sort((a, b) => value(b) - value(a));
  const other = sorted.slice(max).reduce((sum, row) => sum + value(row), 0);
  return [
    ...sorted.slice(0, max),
    { [nameKey]: otherLabel, [valueKey]: other },
  ];
}

/** Text in natural order, numbers by value: "2" before "10". */
export const NATURAL_ORDER = new Intl.Collator(undefined, { numeric: true });

/** An ISO date, alone or with a time: what a Date or Date and time cell holds. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d|$)/;

/** Where a value falls on an axis: its group (Numbers, dates, text, blanks) and place in it. */
function axisKey(value: ReportDatasetRow[string] | undefined) {
  const text = value === null || value === undefined ? '' : String(value);
  if (!text.trim()) return { group: 3, number: 0, text };
  const number = asNumber(value);
  if (Number.isFinite(number)) return { group: 0, number, text };
  const time = ISO_DATE.test(text) ? Date.parse(text) : Number.NaN;
  if (Number.isFinite(time)) return { group: 1, number: time, text };
  return { group: 2, number: 0, text };
}

/**
 * The rows in the order their `key` runs along an axis: Numbers by value,
 * then dates by date, then text, blanks last — as Server aggregation orders
 * a line chart's groups, so a line reads left to right whichever path drew it.
 */
export function inAxisOrder(
  rows: readonly ReportDatasetRow[],
  key: string,
): ReportDatasetRow[] {
  return rows
    .map((row) => ({ row, at: axisKey(row[key]) }))
    .sort(
      (a, b) =>
        a.at.group - b.at.group ||
        a.at.number - b.at.number ||
        NATURAL_ORDER.compare(a.at.text, b.at.text),
    )
    .map(({ row }) => row);
}

/**
 * Groups `rows` by `categoryKey` and sums `valueKey` within each group,
 * yielding one row per distinct category in first-appearance order. Values are
 * coerced to finite numbers (non-numeric → 0) before summing — APIs often
 * serialize numeric columns as strings, which would otherwise sum as text
 * and read as categories on a value axis. The result is
 * the aggregated series a category chart (bar/line) plots.
 */
export function aggregateByCategory(
  rows: ReportDatasetRow[] | undefined,
  categoryKey: string | null,
  valueKey: string | null,
): ReportDatasetRow[] {
  if (!rows || !categoryKey || !valueKey) return [];
  const sums = new Map<string, number>();
  for (const row of rows) {
    const category = String(row[categoryKey] ?? '');
    const raw = row[valueKey];
    const num = typeof raw === 'number' ? raw : Number(raw);
    sums.set(
      category,
      (sums.get(category) ?? 0) + (Number.isFinite(num) ? num : 0),
    );
  }
  return Array.from(sums, ([category, sum]) => ({
    [categoryKey]: category,
    [valueKey]: sum,
  }));
}

/**
 * A category chart's aggregated rows in its sort order, keeping the first
 * `topN` (0 = all). By value, highest or lowest first; by label, as text
 * compares in the viewer's language with numbers in numeric order (so "2"
 * before "10", and ISO dates by date). Ties keep first-appearance order.
 */
export function sortCategories(
  rows: readonly ReportDatasetRow[],
  categoryKey: string | null,
  valueKey: string | null,
  sort: CategorySort,
  topN = 0,
): ReportDatasetRow[] {
  const sorted = [...rows];
  if (categoryKey && valueKey && sort !== 'none') {
    const value = (row: ReportDatasetRow) => Number(row[valueKey] ?? 0) || 0;
    const label = (row: ReportDatasetRow) => String(row[categoryKey] ?? '');
    const compare: Record<
      Exclude<CategorySort, 'none'>,
      (a: ReportDatasetRow, b: ReportDatasetRow) => number
    > = {
      'value-desc': (a, b) => value(b) - value(a),
      'value-asc': (a, b) => value(a) - value(b),
      'label-asc': (a, b) => NATURAL_ORDER.compare(label(a), label(b)),
      'label-desc': (a, b) => NATURAL_ORDER.compare(label(b), label(a)),
    };
    sorted.sort(compare[sort]);
  }
  return topN > 0 ? sorted.slice(0, topN) : sorted;
}
